import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownCircle,
  ArrowUpCircle,
  CheckCircle2,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { api } from "../lib/api";

interface Props {
  competenciaId: number;
}

interface CotaLinha {
  unidade: string;
  status:
    | "EM_DIA"
    | "ANTECIPADO"
    | "ANTECIPACAO"
    | "ATRASO"
    | "INADIMPLENTE"
    | "INDETERMINADO";
  valor: string | null;
  data_pagamento: string | null;
  competencia_referente: string | null;
  competencia_paga_em: string | null;
  dias_atraso: number | null;
  observacao: string;
}

interface CotasResumo {
  regulares: number;
  antecipados: number;
  antecipacoes: number;
  em_atraso: number;
  inadimplentes: number;
  indeterminados: number;
  total_unidades: number;
}

interface CotasResponse {
  competencia_id: number;
  periodo_str: string;
  vencimento_efetivo: string | null;
  linhas: CotaLinha[];
  resumo: CotasResumo;
}

function fmtBRL(v: string | null | undefined): string {
  if (v == null) return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDataBR(iso: string | null): string {
  if (!iso) return "—";
  // iso vem como "2026-02-05"
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const STATUS_CONFIG: Record<
  CotaLinha["status"],
  {
    label: string;
    classes: string;
    badge: string;
    icone: React.ReactNode;
  }
> = {
  EM_DIA: {
    label: "Em dia",
    classes: "bg-emerald-50 hover:bg-emerald-100",
    badge: "bg-emerald-100 text-emerald-800 border-emerald-200",
    icone: <CheckCircle2 size={14} className="text-emerald-600" />,
  },
  ANTECIPADO: {
    label: "Antecipado",
    classes: "bg-sky-50 hover:bg-sky-100",
    badge: "bg-sky-100 text-sky-800 border-sky-200",
    icone: <ArrowDownCircle size={14} className="text-sky-600" />,
  },
  ANTECIPACAO: {
    label: "Antecipação",
    classes: "bg-indigo-50 hover:bg-indigo-100",
    badge: "bg-indigo-100 text-indigo-800 border-indigo-200",
    icone: <ArrowUpCircle size={14} className="text-indigo-600" />,
  },
  ATRASO: {
    label: "Atraso",
    classes: "bg-amber-50 hover:bg-amber-100",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    icone: <AlertTriangle size={14} className="text-amber-600" />,
  },
  INADIMPLENTE: {
    label: "Inadimplente",
    classes: "bg-rose-50 hover:bg-rose-100",
    badge: "bg-rose-100 text-rose-800 border-rose-200",
    icone: <AlertCircle size={14} className="text-rose-600" />,
  },
  INDETERMINADO: {
    label: "Indeterminado",
    classes: "bg-slate-50 hover:bg-slate-100",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    icone: <HelpCircle size={14} className="text-slate-500" />,
  },
};

export function CotasSection({ competenciaId }: Props) {
  const { data, isLoading, error } = useQuery<CotasResponse>({
    queryKey: ["cotas", competenciaId],
    queryFn: () =>
      api.get<CotasResponse>(`/competencias/${competenciaId}/cotas`),
  });

  if (isLoading) {
    return (
      <section className="mt-8">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">
          Cotas condominiais
        </h3>
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Carregando cotas...
        </div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="mt-8">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">
          Cotas condominiais
        </h3>
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-card text-sm text-slate-600">
          Não foi possível carregar as cotas.
        </div>
      </section>
    );
  }

  if (data.linhas.length === 0) {
    return (
      <section className="mt-8">
        <h3 className="text-lg font-semibold text-slate-900 mb-3">
          Cotas condominiais
        </h3>
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-card text-sm text-slate-600">
          Esta competência ainda não tem cotas processadas.
        </div>
      </section>
    );
  }

  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-lg font-semibold text-slate-900">
          Cotas condominiais
        </h3>
        {data.vencimento_efetivo && (
          <span className="text-xs text-slate-500">
            Vencimento: {fmtDataBR(data.vencimento_efetivo)}
          </span>
        )}
      </div>

      <div className="overflow-x-auto bg-white border border-slate-200 rounded-card">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium">Unidade</th>
              <th className="text-left px-4 py-2.5 font-medium">Status</th>
              <th className="text-right px-4 py-2.5 font-medium">Valor</th>
              <th className="text-left px-4 py-2.5 font-medium">
                Data pagamento
              </th>
              <th className="text-left px-4 py-2.5 font-medium">Ref.</th>
              <th className="text-left px-4 py-2.5 font-medium">Observação</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.linhas.map((l, idx) => {
              const cfg = STATUS_CONFIG[l.status];
              return (
                <tr
                  key={`${l.unidade}-${l.competencia_referente ?? "x"}-${idx}`}
                  className={cfg.classes}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {l.unidade}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs border ${cfg.badge}`}
                    >
                      {cfg.icone}
                      {cfg.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {fmtBRL(l.valor)}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {fmtDataBR(l.data_pagamento)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-600">
                    {l.competencia_referente ?? "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {l.observacao}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-50 border-t-2 border-slate-200">
            <tr>
              <td colSpan={6} className="px-4 py-3">
                <div className="flex flex-wrap gap-2 items-center text-xs">
                  <ChipResumo
                    valor={data.resumo.regulares}
                    label="Regulares"
                    cfg={STATUS_CONFIG.EM_DIA}
                  />
                  <ChipResumo
                    valor={data.resumo.antecipados}
                    label="Antecipados"
                    cfg={STATUS_CONFIG.ANTECIPADO}
                  />
                  {data.resumo.antecipacoes > 0 && (
                    <ChipResumo
                      valor={data.resumo.antecipacoes}
                      label="Antecipações p/ próximo mês"
                      cfg={STATUS_CONFIG.ANTECIPACAO}
                    />
                  )}
                  <ChipResumo
                    valor={data.resumo.em_atraso}
                    label="Em atraso"
                    cfg={STATUS_CONFIG.ATRASO}
                  />
                  <ChipResumo
                    valor={data.resumo.inadimplentes}
                    label="Inadimplentes"
                    cfg={STATUS_CONFIG.INADIMPLENTE}
                  />
                  {data.resumo.indeterminados > 0 && (
                    <ChipResumo
                      valor={data.resumo.indeterminados}
                      label="Indeterminados"
                      cfg={STATUS_CONFIG.INDETERMINADO}
                    />
                  )}
                  <span className="ml-auto text-slate-700">
                    <span className="font-semibold tabular-nums">
                      {data.resumo.total_unidades}
                    </span>{" "}
                    unidades no total
                  </span>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}

function ChipResumo({
  valor,
  label,
  cfg,
}: {
  valor: number;
  label: string;
  cfg: (typeof STATUS_CONFIG)[CotaLinha["status"]];
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border ${cfg.badge}`}
    >
      {cfg.icone}
      <span className="font-semibold tabular-nums">{valor}</span>
      <span>{label}</span>
    </span>
  );
}
